import React from "react";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { depositLadderStore, depositLadderArchiveStore, depositLadderFormStore, createDepositLadderForm, formatClaimPeriod } from "../stores/depositLadder";
import DepositLadderAnalytics from "./DepositLadderAnalytics";

const formatDateTime = (value) => {
	const date = new Date(value);

	return value && !Number.isNaN(date.getTime()) ? `${date.toLocaleDateString()} ${date.toLocaleTimeString()}` : "-";
};

const normalizeStepsForForm = (steps) =>
	(Array.isArray(steps) && steps.length ? steps : createDepositLadderForm().steps).map((step) => ({
		...step,
		rewardType: step.rewardType || "deposit",
		fsGameId: step.fsGameId ?? null,
		fsBetAmount: step.fsBetAmount ?? null,
		fsExpirationDays: step.fsExpirationDays ?? null,
		fsParts: Array.isArray(step.fsParts)
			? step.fsParts.map((part) => ({
					...part,
					_rowId: crypto.randomUUID(),
				}))
			: null,
	}));

class DepositLadder extends React.Component {
	state = {
		activeTabId: "ladders",
		archiveCandidate: null,
		actionLoading: false,
	};

	componentDidMount() {
		this.props.Interface.loadDepositLadderList();
	}

	handleTabChange = ({ detail }) => {
		this.setState({ activeTabId: detail.activeTabId });

		if (detail.activeTabId === "ladders" && !this.props.depositLadder.loaded) {
			this.props.Interface.loadDepositLadderList();
		}

		if (detail.activeTabId === "archive" && !this.props.depositLadderArchive.loaded) {
			this.props.Interface.loadDepositLadderList(undefined, true);
		}
	};

	openEdit = (item) => {
		this.props.setForm({ ...createDepositLadderForm(), show: true, loaded: false, formMode: "edit", id: item.id });
		this.props.Interface.request("get", { class: "data", module: "depositLadder", method: "get" }, { id: item.id })
			.then((data) =>
				this.props.setForm({
					...createDepositLadderForm(),
					...data,
					...formatClaimPeriod(data.claimPeriod),
					partnerUids: (data.partnerUids || []).join(", "),
					steps: normalizeStepsForForm(data.steps),
					show: true,
					loaded: true,
					formMode: "edit",
				}),
			)
			.catch((error) => {
				this.props.Interface.addNoty("Error", error?.text || error.toString());
				this.props.setForm(createDepositLadderForm());
			});
	};

	duplicate = (item) => {
		this.props.setForm({ ...createDepositLadderForm(), show: true, loaded: false, formMode: "duplicate" });
		this.props.Interface.request("get", { class: "data", module: "depositLadder", method: "get" }, { id: item.id })
			.then((data) => {
				const duplicate = {
					...createDepositLadderForm(),
					...data,
					...formatClaimPeriod(data.claimPeriod),
					id: null,
					name: `${data.name} (copy)`,
					active: 0,
					partnerUids: (data.partnerUids || []).join(", "),
					steps: normalizeStepsForForm(data.steps),
					show: true,
					loaded: true,
					formMode: "duplicate",
				};
				delete duplicate.createdAt;
				delete duplicate.updatedAt;
				delete duplicate.archived;
				this.props.setForm(duplicate);
			})
			.catch((error) => {
				this.props.Interface.addNoty("Error", error?.text || error.toString());
				this.props.setForm(createDepositLadderForm());
			});
	};

	markListForReload = (list, setList) => {
		setList({ ...list, loaded: false });
	};

	reloadList = (list, setList, archived) => {
		setList({
			...list,
			loaded: false,
			pagination: { ...list.pagination, current: 1 },
		});
		setTimeout(() => this.props.Interface.loadDepositLadderList(undefined, archived), 0);
	};

	archive = () => {
		const item = this.state.archiveCandidate;

		if (!item || this.state.actionLoading) {
			return;
		}

		this.setState({ actionLoading: true });
		this.props.Interface.request("set", { class: "data", module: "depositLadder", method: "archive" }, { id: item.id })
			.then(() => {
				this.props.Interface.addNoty("Success", "Deposit ladder was archived");
				this.setState({ archiveCandidate: null, actionLoading: false });
				this.reloadList(this.props.depositLadder, this.props.setDepositLadder, false);
				this.markListForReload(this.props.depositLadderArchive, this.props.setDepositLadderArchive);
			})
			.catch((error) => {
				this.props.Interface.addNoty("Error", error?.text || error.toString());
				this.setState({ actionLoading: false });
			});
	};

	restore = (item) => {
		if (this.state.actionLoading) {
			return;
		}

		this.setState({ actionLoading: true });
		this.props.Interface.request("set", { class: "data", module: "depositLadder", method: "restore" }, { id: item.id })
			.then(() => {
				this.props.Interface.addNoty("Success", "Deposit ladder was restored as inactive");
				this.setState({ actionLoading: false });
				this.reloadList(this.props.depositLadderArchive, this.props.setDepositLadderArchive, true);
				this.markListForReload(this.props.depositLadder, this.props.setDepositLadder);
			})
			.catch((error) => {
				this.props.Interface.addNoty("Error", error?.text || error.toString());
				this.setState({ actionLoading: false });
			});
	};

	renderLaddersTable(list, setList, archived = false) {
		const loadList = () => this.props.Interface.loadDepositLadderList(undefined, archived);
		const table = (
			<Table
				stickyHeader
				trackBy="id"
				loading={!list.loaded}
				items={list.data}
				sortingColumn={{ sortingField: list.sorting.sortingColumn.column }}
				sortingDescending={list.sorting.isDescending}
				onSortingChange={(sorting) => {
					setList({
						...list,
						sorting: {
							sortingColumn: {
								order: sorting.detail.isDescending ? "DESC" : "ASC",
								column: sorting.detail.sortingColumn.sortingField,
							},
							isDescending: sorting.detail.isDescending,
						},
						pagination: { ...list.pagination, current: 1 },
					});
					setTimeout(loadList, 0);
				}}
				columnDefinitions={[
					{ id: "id", header: "ID", sortingField: "id", cell: (item) => item.id },
					{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
					{ id: "mode", header: "Mode", sortingField: "mode", cell: (item) => item.mode },
					{ id: "partnerUids", header: "Partner UIDs", cell: (item) => (item.partnerUids || []).join(", ") || "-" },
					{ id: "steps", header: "Steps", cell: (item) => item.stepCount || "-" },
					{
						id: "claimPeriod",
						header: "Claim period",
						cell: (item) => {
							const period = formatClaimPeriod(item.claimPeriod);

							return `${period.claimPeriod} ${period.claimPeriodUnit}`;
						},
					},
					{ id: "active", header: "Active", sortingField: "active", cell: (item) => (Number(item.active) ? "Enabled" : "Disabled") },
					{ id: "updatedAt", header: "Updated", sortingField: "updatedAt", cell: (item) => formatDateTime(item.updatedAt) },
					{
						id: "actions",
						header: "Actions",
						cell: (item) => (
							<SpaceBetween direction="horizontal" size="s">
								{archived ? <Button wrapText={false} loading={this.state.actionLoading} onClick={() => this.restore(item)}>Restore</Button> : null}
								{!archived ? <Button wrapText={false} onClick={() => this.openEdit(item)}>Edit</Button> : null}
								{!archived ? <Button wrapText={false} disabled={Number(item.active) === 1} loading={this.state.actionLoading} onClick={() => this.setState({ archiveCandidate: item })}>Archive</Button> : null}
								<Button wrapText={false} onClick={() => this.duplicate(item)}>Duplicate</Button>
							</SpaceBetween>
						),
						minWidth: archived ? 200 : 280,
					},
				]}
				pagination={
					<Pagination
						currentPageIndex={list.pagination.current}
						pagesCount={list.pagination.total}
						onChange={({ detail }) => {
							setList({ ...list, pagination: { ...list.pagination, current: detail.currentPageIndex } });
							setTimeout(loadList, 0);
						}}
					/>
				}
				header={
					<Header
						actions={
							archived ? null : (
								<SpaceBetween direction="horizontal">
									<Button variant="primary" onClick={() => this.props.setForm({ ...createDepositLadderForm(), show: true, formMode: "add" })}>
										Add deposit ladder
									</Button>
								</SpaceBetween>
							)
						}>
						{archived ? "Archive" : "Deposit Ladder"} ({list.pagination.count})
					</Header>
				}
				empty={archived ? "No archived deposit ladders found" : "No deposit ladders found"}
			/>
		);

		return list.error?.text ? (
			<SpaceBetween size="m">
				<Alert type="error">{list.error.text}</Alert>
				{table}
			</SpaceBetween>
		) : table;
	}

	renderArchiveConfirmation() {
		return (
			<Modal
				visible={Boolean(this.state.archiveCandidate)}
				onDismiss={() => !this.state.actionLoading && this.setState({ archiveCandidate: null })}
				header="Archive deposit ladder"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={this.state.actionLoading} onClick={() => this.setState({ archiveCandidate: null })}>Cancel</Button>
							<Button variant="primary" loading={this.state.actionLoading} onClick={this.archive}>Archive</Button>
						</SpaceBetween>
					</Box>
				}>
				Archiving <b>{this.state.archiveCandidate?.name}</b> will disable it and stop all new assignments. Existing player assignments will continue to work.
			</Modal>
		);
	}

	render() {
		return (
			<>
				<Tabs
					activeTabId={this.state.activeTabId}
					onChange={this.handleTabChange}
					tabs={[
						{
							id: "ladders",
							label: "Ladders",
							content: this.renderLaddersTable(this.props.depositLadder, this.props.setDepositLadder),
						},
						{
							id: "archive",
							label: "Archive",
							content: this.renderLaddersTable(this.props.depositLadderArchive, this.props.setDepositLadderArchive, true),
						},
						{
							id: "analytics",
							label: "Analytics",
							content: <DepositLadderAnalytics Interface={this.props.Interface} />,
						},
					]}
				/>
				{this.renderArchiveConfirmation()}
			</>
		);
	}
}

export default function DepositLadderWithRecoil(props) {
	const depositLadder = useRecoilValue(depositLadderStore);
	const depositLadderArchive = useRecoilValue(depositLadderArchiveStore);
	const setDepositLadder = useSetRecoilState(depositLadderStore);
	const setDepositLadderArchive = useSetRecoilState(depositLadderArchiveStore);
	const setForm = useSetRecoilState(depositLadderFormStore);
	return <DepositLadder {...props} depositLadder={depositLadder} depositLadderArchive={depositLadderArchive} setDepositLadder={setDepositLadder} setDepositLadderArchive={setDepositLadderArchive} setForm={setForm} />;
}
